/**
 * KAN-751: блок "Инструкции оператора" из частей по реестру.
 *
 * Компонент управляемый: значения частей хранит форма (useUserEditForm),
 * здесь только рендер частей, превью и перенос старого текста.
 */
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { LegacyPromptBlock } from './LegacyPromptBlock';
import {
  isPromptSectionsEmpty,
  sectionMaxLength,
  useOperatorSectionsRegistry,
  usePromptSectionsPreview,
  type PromptSectionsErrors,
} from '@/hooks/useOperatorSections';
import type { PromptSections } from '@/types/user';

export const TOOL_NOTE_TEXT = 'система сама вызовет нужное действие, писать название не нужно';
const EXTRA_KEY = 'extra';
export const PREVIEW_LEGACY_TEXT = 'используется старый формат';

interface OperatorSectionsEditorProps {
  userId: string;
  value: PromptSections;
  onChange: (next: PromptSections) => void;
  /** Старый единый текст инструкций (user_prompt), только для чтения. */
  legacyPrompt?: string;
  errors?: PromptSectionsErrors;
}

function AutoHeightTextarea({
  className,
  ...props
}: React.ComponentProps<typeof Textarea>): JSX.Element {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    if (el.scrollHeight > 0) el.style.height = `${el.scrollHeight}px`;
  }, [props.value]);
  return (
    <Textarea
      ref={ref}
      rows={2}
      className={cn('resize-none overflow-hidden', className)}
      {...props}
    />
  );
}

export function OperatorSectionsEditor({
  userId,
  value,
  onChange,
  legacyPrompt = '',
  errors,
}: OperatorSectionsEditorProps): JSX.Element {
  const { data: sections = [], isLoading } = useOperatorSectionsRegistry();
  const preview = usePromptSectionsPreview(userId);
  const [previewOpen, setPreviewOpen] = useState(false);

  const setSection = useCallback(
    (key: string, text: string) => onChange({ ...value, [key]: text }),
    [value, onChange]
  );

  const sectionsEmpty = isPromptSectionsEmpty(value);

  const handleMoveToExtra = useCallback(() => {
    setSection(EXTRA_KEY, legacyPrompt);
  }, [legacyPrompt, setSection]);

  const handlePreview = useCallback(() => {
    if (previewOpen) {
      setPreviewOpen(false);
      return;
    }
    setPreviewOpen(true);
    preview.mutate(value);
  }, [previewOpen, preview, value]);

  return (
    <div data-testid="operator-sections" className="space-y-3">
      <Label className="text-sm font-medium">Инструкции оператора</Label>

      {errors?.general && (
        <p data-testid="operator-sections-error" className="text-xs text-destructive">
          {errors.general}
        </p>
      )}

      {legacyPrompt.trim().length > 0 && (
        <LegacyPromptBlock
          text={legacyPrompt}
          inUse={sectionsEmpty}
          onMoveToExtra={handleMoveToExtra}
        />
      )}

      {isLoading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Загрузка частей...
        </div>
      )}

      {sections.map(section => {
        const id = `operator-section-${section.key}`;
        const error = errors?.byKey[section.key];
        return (
          <div key={section.key} data-testid={id} className="space-y-1">
            <Label htmlFor={id}>{section.title}</Label>
            {section.hint && (
              <p id={`${id}-hint`} className="text-xs text-muted-foreground">
                {section.hint}
              </p>
            )}
            <AutoHeightTextarea
              id={id}
              data-testid={`${id}-input`}
              value={value[section.key] ?? ''}
              onChange={e => setSection(section.key, e.target.value)}
              maxLength={sectionMaxLength(section)}
              aria-describedby={section.hint ? `${id}-hint` : undefined}
              aria-invalid={error ? true : undefined}
              className={error ? 'border-destructive' : undefined}
            />
            {section.has_tool_note && (
              <p data-testid={`${id}-tool-note`} className="text-[11px] text-muted-foreground">
                {TOOL_NOTE_TEXT}
              </p>
            )}
            {error && (
              <p data-testid={`${id}-error`} className="text-xs text-destructive">
                {error}
              </p>
            )}
          </div>
        );
      })}

      <div className="space-y-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="operator-sections-preview-button"
          aria-expanded={previewOpen}
          onClick={handlePreview}>
          Как это увидит бот
        </Button>
        {previewOpen && (
          <div
            data-testid="operator-sections-preview"
            className="rounded-md border border-border bg-gray-50 p-3 text-sm">
            {preview.isPending && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> Собираем текст...
              </div>
            )}
            {preview.isError && (
              <p className="text-xs text-destructive">Не удалось собрать превью</p>
            )}
            {preview.isSuccess && preview.data.legacy && (
              <p data-testid="operator-sections-preview-legacy" className="mb-2 text-xs text-amber-700">
                {PREVIEW_LEGACY_TEXT}
              </p>
            )}
            {preview.isSuccess &&
              (preview.data.text ? (
                <pre className="whitespace-pre-wrap font-sans">{preview.data.text}</pre>
              ) : (
                <p className="text-xs text-muted-foreground">Инструкции пусты</p>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default OperatorSectionsEditor;
